import services from './services.js'
import Baslide from './module.baslide.js'
import { GET_PRODUCT_BY_HANDLE } from './graphql.queries.js'

class ProductCard {
  constructor(element) {
    this.element = element

    this.$title = this.element.querySelector('[data-block="title"]')
    this.$color = this.element.querySelector('[data-block="color"]')
    this.$price = this.element.querySelector('[data-block="price"]')
    this.$carousel = this.element.querySelector('[data-block="carousel"]')
    this.$swatchGroup = this.element.querySelector('[data-block="swatch-group"]')
    this.$swatches = this.element.querySelectorAll('[data-block="swatch"]')
    this.$badgeGroup = this.element.querySelector('[data-block="badge-group"]')
    this.$sizeStack = this.element.querySelector('[data-block="size-stack"]')
    this.$sizeWrapper = this.element.querySelector('[data-block="size-wrapper"]')
    this.$sizeGroups = this.element.querySelectorAll('[data-block="size-group"]')
    this.$sizes = this.element.querySelectorAll('[data-block="size"]')
    this.$btnAddToCart = this.element.querySelector('[data-block="add-to-cart"]')
    this.$form = this.element.querySelector('form')

    this.state = {
      locked: false,
    }

    this.connectedCallback()
  }

  // Life cycle methods START
  connectedCallback() {
    if (this.element.initialized) return

    this.initCarousel()
    this.initSwatchesSlide()

    this.fetchProductSwatches((swatches) => {
      this.renderSwatches(swatches)
      this.renderSizes(swatches)
    })

    this.subscribeSwatches()
    this.subscribeSizes()
    this.subscribeGestures()

    this.element.initialized = true
  }

  disconnectedCallback() {
    this.element.initialized = false
    this.element.carousel?.destroy()
    this.element.swatchesCarousel?.disconnect()

    this.unsubscribeSwatches()
    this.unsubscribeSizes()
    this.unsubscribeGestures()
  }
  // Life cycle methods END

  // Listeners START
  subscribeSwatches() {
    this.unsubscribeSwatches()
    this.$swatches.forEach(($swatch) => {
      $swatch.addEventListener('click', this.handleChangeSwatch)
    })
  }

  unsubscribeSwatches() {
    this.$swatches.forEach(($swatch) => {
      $swatch.removeEventListener('click', this.handleChangeSwatch)
    })
  }

  subscribeSizes() {
    this.unsubscribeSizes()
    this.element.querySelectorAll('[data-block="size"]').forEach(($size) => {
      const $input = $size.querySelector('input')
      $input.addEventListener('change', this.handleChangeSize)
    })
  }

  unsubscribeSizes() {
    this.element.querySelectorAll('[data-block="size"]').forEach(($size) => {
      const $input = $size.querySelector('input')
      $input.removeEventListener('change', this.handleChangeSize)
    })
  }

  subscribeGestures() {
    document.body.addEventListener('click', (event) => this.onOutsideClick(event.target))
    window.addEventListener('scroll', this.onWindowScroll, { passive: true })
    this.element.addEventListener('touchstart', this.handleTouchStart, { passive: true })
    this.element.addEventListener('touchend', this.handleTouchEnd, { passive: true })
    this.element.addEventListener('mouseenter', this.handleHover, { passive: true })
    clearTimeout(this.scrollTimeout)
  }

  unsubscribeGestures() {
    document.body.removeEventListener('click', (event) => this.onOutsideClick(event.target))
    window.removeEventListener('scroll', this.onWindowScroll, { passive: true })
    this.element.removeEventListener('touchstart', this.handleTouchStart, { passive: true })
    this.element.removeEventListener('touchend', this.handleTouchEnd, { passive: true })
    this.element.removeEventListener('mouseenter', this.handleHover, { passive: true })
  }

  // Listeners END

  // Getters START
  get swatchesHandles() {
    return Array.from(this.$swatches).map((el) => el.dataset.handle)
  }

  get activeSwatchData() {
    const $swatch = Array.from(this.$swatches).find(($sw) => $sw.dataset.handle === this.handle)
    return {
      title: this.$title.textContent,
      color: $swatch.dataset.color,
      images: String($swatch.dataset.images).split(','),
      price: parseFloat($swatch.dataset.price || '0'),
      compareAtPrice: parseFloat($swatch.dataset.compareAtPrice || '0'),
      tags: String($swatch.dataset.tags).split(','),
      handle: $swatch.dataset.handle,
    }
    
  }

  get activeSize() {
    const $size = Array.from(this.$sizes).find(($size) => $size.querySelector('input:checked'))
    return $size?.querySelector('input').value
  }

  get handle() {
    return this.element.getAttribute('handle')
  }
  // Getters END

  // Fetchers START
  fetchProductSwatches(callback) {
    const handles = this.swatchesHandles
    try {
      let queries = []

      for (let index = 0; index < handles.length; index++) {
        const handle = handles[index]
        let newQuery = GET_PRODUCT_BY_HANDLE
        newQuery = newQuery.replace('query getProductByHandle($handle: String){', '')
        newQuery = newQuery.slice(0, -1)
        newQuery = newQuery.replace('product(handle: $handle)', `product${index}: product(handle: $handle)`).replace('$handle', '"' + handle + '"')
        newQuery = newQuery.replace(/\n/gm, '')
        newQuery = newQuery.trim()
        queries.push(newQuery)
      }

      const query = `{${queries.join('')}}`

      services.graphql.use({ query }, { cache: 'force-cache' }).then(({ data }) => {
        if (!data) data = {}
        const products = Object.values(data).reduce((acc, product) => {
          if (!product) return acc
          acc[product.handle] = product

          return acc
        }, {})

        callback?.(products)
      })
    } catch (error) {
      console.log(error)
    }
  }
  // Fetchers END

  // Handlers START
  handleChangeSwatch = (e) => {
    let handle = null
    if (typeof e !== 'string') {
      const target = e.target.closest('[data-block="swatch"]')
      handle = target.dataset.handle
    } else {
      handle = e
    }

    if (!this.element.initialized) return
    this.$form?.reset()

    this.element.setAttribute('handle', handle)

    this.renderImages()
    this.renderBadges()
    this.renderPrice()
    this.renderActiveSize()
    this.renderBuyButtons()
    this.renderGeneral()
  }

  handleChangeSize = () => {
    this.renderBuyButtons()
  }

  handleTouchStart = () => {
    this.state.locked = true
    this.handleHover()
  }

  handleTouchEnd = () => {
    this.state.locked = false
  }

  handleHover = () => {
    this.$carousel.querySelectorAll('img').forEach((image) => (image.loading = 'eager'))
  }

  // Outside click of product card Handle
  onOutsideClick(target) {
    // function hasClass(element, cls) {
    //   return (' ' + element?.className + ' ').indexOf(' ' + cls + ' ') > -1;
    // }
      const elemClick = document.querySelector('.product-grid-selector')
      // const dataLayout = $('.product-grid-selector ').classList.contains('grid-cols--1');
      const dataLayoutClick = $('.product-grid-selector').hasClass('grid-cols--1')
    if(!dataLayoutClick){
      if (!this.element.contains(target)) {
        if (this.$sizeStack.classList.contains('collapse--active')) {
          this.$sizeStack.querySelector('.collapse__button')?.click()
        }
      }
    }

    
  }

  // Window scroll handle
  onWindowScroll = (e) => {
    // function hasClass(element, cls) {
    //     return (' ' + element?.className + ' ').indexOf(' ' + cls + ' ') > -1;
    // }
    const elemScroll = document.querySelector('.product-grid-selector')
    // const dataLayout = $('.product-grid-selector ').classList.contains('grid-cols--1');
    const dataLayoutScroll = $('.product-grid-selector').hasClass('grid-cols--1')
    if(!dataLayoutScroll){
      if (!this.state.locked && this.$sizeStack.classList.contains('collapse--active') && !this.scrolling) {
        this.scrolling = true
  
        this.scrollTimeout = setTimeout(() => {
          this.onOutsideClick(document.body)
          this.scrolling = false
        }, 3000)
      }
    }
    
  }
  // Handlers END

  // Renderers START
  initCarousel() {
    this.element.carousel = new Swiper(this.$carousel, {
      navigation: {
        nextEl: ".swiper-button-next",
        prevEl: ".swiper-button-prev",
      },
      pagination: {
        el: ".swiper-pagination",
        clickable: true,
      }
    })
  }

  initSwatchesSlide() {
    this.element.swatchesCarousel = new Baslide(this.$swatchGroup, {
      perPage: 6,
      perMove: 4,
      responsive: {
        768: {
          perPage: 4,
          perMove: 2,
        },
      },
    })
  }

  renderInitialSwatch(swatches) {
    const currentUrl = window.location.href
    const queryParams = new URLSearchParams(new URL(currentUrl).search)
    const sizeFilter = queryParams.getAll('filter.v.option.size')
    const colorFilter = queryParams.get('filter.p.m.custom.group_colors')
    this.$swatches.forEach(($swatch) => {
      const handle = $swatch.dataset.handle
      const swatch = swatches[handle]
      if(!swatch) return
      let colors = swatch.metafields[0]?.value;

      if(colors){
        colors = JSON.parse(colors)
        colors = colors.map(i => {
          if (i === 'marrón') i = 'marron'
          return i
         });
      }
      

      if (colorFilter != null) {
        setTimeout(() => {
          $('.product-card__column').css('opacity' , '1');
        },500)
          if(!colors?.includes(colorFilter)){
            $swatch.closest('.product-card__column').classList.add('remove')
            $swatch.parentElement.remove()
            setTimeout(() => {
              this.element.querySelector('.baslide__track').querySelectorAll('.baslide__item')[0]?.querySelector('.product-card__swatch')?.click()
            },1)  
            const removed = document.querySelectorAll('.product-card__column');
            removed.forEach(element => {
            const count = element.querySelectorAll('.baslide__item').length;
            const cardCount = document.querySelectorAll('product-grid .product-card__column').length;
            if(count < 6){
              element.classList.add('hide-arrows');
            }
            if(count < 1){
              
              element.remove();
            }
            setTimeout(() => {
              $('html').removeClass('o-hidden')
              $('.filter_loader').fadeOut()
            }, 800)

            if(cardCount < 1){
              const productGridLoop = document.querySelector('product-grid');
              const currentCollection = document.querySelector('product-grid').getAttribute('data-collection');
              const emptyStateHTML = `
                  <div class="EmptyState">
                    <div class="Container">
                      <span class="EmptyState__Title Heading u-h5">No products</span>
                      <a href="/collections/${currentCollection}" class="EmptyState__Action Button Button--primary">Reset filters</a>
                    </div>
                  </div>`;

                
                productGridLoop.innerHTML = emptyStateHTML;
            }
          });
          } else {
            if (sizeFilter.length){
        
              sizeFilter.forEach((element) => {
              const index = swatch.variants['nodes'].findIndex((variant) => variant.title === element)
              const checkAvaliable = swatch.variants['nodes'][index]?.availableForSale         
                  if (!checkAvaliable){
                    const swatchP = $swatch.parentElement.parentElement;
                    $swatch.closest('.product-card__column').classList.add('remove')
                  
                  }    
                          
              
              })
               
            }
          }
      }
      
      
      if (sizeFilter.length){
     
        setTimeout(() => {
          $('.product-card__column').css('opacity' , '1');
        },500)
        sizeFilter.forEach((element) => {
        const index = swatch.variants['nodes'].findIndex((variant) => variant.title === element)
        const checkAvaliable = swatch.variants['nodes'][index]?.availableForSale;
        // console.log( typeof checkAvaliable,'available',$swatch.parentElement.parentElement)


        // if (typeof checkAvaliable !== 'undefined' ) {
        //   // console.log($swatch.parentElement, 'target');
        //   $swatch.parentElement.classList.add('at-least-one');
          
        // }
       
        if(checkAvaliable === true){
          $swatch.parentElement.classList.add('at-least-one');
        }
        // if(checkAvaliable == undefined){
        //   $swatch.parentElement.classList.remove('at-least-one');
        // }
        if (checkAvaliable == undefined || checkAvaliable == false ) {
          const productHandle = this.element.getAttribute('data-handle')
          const swatchHandle = $swatch.dataset.handle
          //hasremove

          setTimeout(() => {
            if (!$swatch.parentElement.classList.contains('at-least-one')) {
              $swatch.parentElement.remove()
            }
          },100)
          
          const removed = document.querySelectorAll('.product-card__column');
          const cardCount = document.querySelectorAll('product-grid .product-card__column').length;
          setTimeout(() => {
            $('html').removeClass('o-hidden')
            $('.filter_loader').fadeOut()
          }, 800)
          if(cardCount < 1){
            const productGridLoop = document.querySelector('product-grid');
            const currentCollection = document.querySelector('product-grid').getAttribute('data-collection');
            const emptyStateHTML = `
                <div class="EmptyState">
                  <div class="Container">
                    <h1 class="EmptyState__Title Heading u-h5">No products</h1>
                    <a href="/collections/${currentCollection}" class="EmptyState__Action Button Button--primary">Reset filters</a>
                  </div>
                </div>`;

              
              productGridLoop.innerHTML = emptyStateHTML;
          }

     
          removed.forEach(element => {
           setTimeout(() => {
           // Select all .baslide__item elements
           const items = element.querySelectorAll('.baslide__item');
           
           // Filter out items that contain an element with the class .product-card__swatch
           const count = Array.from(items).filter(item => item.querySelector('.product-card__swatch')).length;
       
       
           // Perform actions based on the count
           if (count < 1) {
            //hasremove
              //  
              //recover
                element.remove();
              
           }
           if (count < 6) {
               element.classList.add('hide-arrows');
           }
           },150)
            
          
            
          });
        }
        if (checkAvaliable != undefined && checkAvaliable != true) {
          const productHandle = this.element.getAttribute('data-handle')
          const swatchHandle = $swatch.dataset.handle
          //hasremove
          // 

          setTimeout(() => {
            if (!$swatch.parentElement.classList.contains('at-least-one')) {
              $swatch.parentElement.remove()
            }
          },100)
          
          const swatchList = this.element.querySelectorAll('.baslide__track .baslide__item .product-card__swatch')
          let swatchlist = []
          swatchList.forEach((element) => {
            swatchlist.push(element.getAttribute('data-handle'))
          })
          if (productHandle === swatchHandle) {
            // const filteredSwatches = swatchlist.filter((item) => item !== `${swatchHandle}`)
            // this.element.setAttribute('data-handle', filteredSwatches[0])
            
            setTimeout(() => {
              this.element.querySelector('.baslide__track').querySelectorAll('.baslide__item')[0]?.querySelector('.product-card__swatch')?.click()
            },10)
          }
        }
        })
         
      }

      
      
      
    });
//recover
    setTimeout(() => {
      const removed = document.querySelectorAll('.remove');
    removed.forEach(element => {
      const count = element.querySelectorAll('.product-card__swatch--active').length;
      console.log(count,element)
      if(count < 1){
        
        element.remove();
      }
    });
    },1000)
    
  }
  

  renderSwatches(swatches) {
   
    this.$swatches.forEach(($swatch) => {
      let width = window.innerWidth / 2.6
      if (window.innerWidth <= 768) width = window.innerWidth / 1.5
      width = parseInt(width)

      const handle = $swatch.dataset.handle
      const swatch = swatches[handle]
      if(!swatch){
        $swatch.parentElement.removeChild($swatch);
        return
      }
      const color = swatch.title.split('|').at(-1)
      const images = swatch.images.nodes.map((image) => `${image.url}&width=${width}`)
      const featuredImage = `${swatch.featuredImage.url}&width=150`

      const variants = swatch.variants.nodes
      const currentVariant = variants?.[0]
      const price = Number(currentVariant?.price?.amount || 0) * 100
      const compareAtPrice = Number(currentVariant?.compareAtPrice?.amount || 0) * 100

      $swatch.dataset.images = images
      $swatch.dataset.color = color
      $swatch.dataset.price = price
      $swatch.dataset.compareAtPrice = compareAtPrice
      $swatch.dataset.tags = swatch.tags
      let hideClass = '';
      if(swatch.tags.includes('hide-germany')){
         hideClass = 'hide-germany-swatch';
      }

      $swatch.innerHTML = `
        <img src="${featuredImage}" alt="${color}" class="product-card__swatch__image ${hideClass}" width="50" height="50" loading="lazy">
      `
    })

    this.renderInitialSwatch(swatches)
  }

  renderSizes(swatches) {
    let handles = this.swatchesHandles
    if (this.$sizeWrapper?.querySelector('[data-block="size-group"]')) {
      handles = handles.filter((handle) => handle !== this.handle)
    }

    handles.forEach((handle) => {
      const $group = document.createElement('div')
      $group.classList.add('size-button__group', 'hidden')
      $group.dataset.block = 'size-group'
      $group.dataset.handle = handle

      const variants = swatches[handle]?.variants?.nodes || []

      const content = variants.map((variant) => {
        const id = ProductCard.getVariantId(variant.id)
        const available = variant.available || variant.availableForSale

        return `
          <label class="size-button" data-block="size">
            <input type="radio" name="id" value="${id}" ${available ? '' : 'disabled'}>
            <div class="size-button__content"><span>EU</span><span>${variant.title}</span></div>
          </label>
        `
      })

      $group.innerHTML = content.join('')
      this.$sizeWrapper?.append($group)

      this.$sizeGroups = this.element.querySelectorAll('[data-block="size-group"]')
      this.$sizes = this.element.querySelectorAll('[data-block="size"]')

      this.subscribeSizes()
    })
  }

  renderActiveSize() {
    this.$sizeGroups.forEach(($el) => {
      if ($el.dataset.handle !== this.handle) {
        $el.classList.add('hidden')
      } else {
        $el.classList.remove('hidden')
      }
    })
  }

  renderBadges() {
    const { tags, price, compareAtPrice } = this.activeSwatchData
    const badges = tags.filter((tag) => tag.includes('LABEL_'))

    const content = badges.map((badge) => {
      return `<div class="product-card__badge">${window.languages[badge]}</div>`
    })

    if (price < compareAtPrice) {
      const discount = (price * 100) / compareAtPrice
      const discountPercent = Math.round(100 - discount)
      content.push(`<div class="product-card__badge discount__badge">${window.languages['LABEL_discount']} ${discountPercent}%</div>`)
    }

    this.$badgeGroup.innerHTML = content.join('')
  }

  renderImages() {
    this.element.carousel?.destroy()

    const { images, title, handle } = this.activeSwatchData
    const content = images.map((image, index) => {
      let width = window.innerWidth / 2.6
      if (window.innerWidth <= 768) width = window.innerWidth / 1.5
      width = parseInt(width)
      var newWidth = 738

      var newImage = image.split('&width=')[0]

      var newImageUrl = newImage + '&width=' + newWidth


      return `
        <div class="swiper-slide">
              <a 
              href="${this.element.dataset.collection}/products/${handle}"
              data-url="/products/${handle}"
              rel="nofollow"
              class="product-card__thumbnail AspectRatio AspectRatio--withFallback"
              style="--aspect-ratio: 1;" 
            >
              <img
                src="${newImageUrl}"
                alt="${title}"
                width="200"
                height="200"
                loading="eager"
              >
          </a>
        </div>
      `
    })
    const wrapperElement = document.createElement('div')
    wrapperElement.classList.add('swiper-wrapper')
  
    // Append the content to the wrapper element
    wrapperElement.innerHTML = content.join('')
  
    // Clear any existing content in the carousel element
    this.$carousel.innerHTML = ''
  
    // Append the wrapper element to the carousel element
    this.$carousel.appendChild(wrapperElement)
  
    // Create the next button element
    const nextButtonElement = document.createElement('div')
    nextButtonElement.classList.add('swiper-button-next')
    nextButtonElement.innerHTML = `<svg viewBox='0 0 100 100'>
    <path d="M 10,50 L 60,100 L 70,90 L 30,50  L 70,10 L 60,0 Z" class="arrow" transform="translate(100, 100) rotate(180) "></path>
  </svg>`
  
    // Create the previous button element
    const prevButtonElement = document.createElement('div')
    prevButtonElement.classList.add('swiper-button-prev')
    prevButtonElement.style.opacity = 0
    prevButtonElement.innerHTML=`<svg viewBox='0 0 100 100'>
    <path d="M 10,50 L 60,100 L 70,90 L 30,50  L 70,10 L 60,0 Z" class="arrow"></path>
  </svg>`

  const swiperPagination = document.createElement('div')
  swiperPagination.classList.add('swiper-pagination')
  
    // Append the next and previous button elements after the wrapper element
    this.$carousel.appendChild(nextButtonElement)
    this.$carousel.appendChild(prevButtonElement)
    this.$carousel.appendChild(swiperPagination)
  
    this.initCarousel()
  }

  renderPrice() {
    const { price, compareAtPrice, handle } = this.activeSwatchData
    const priceVaries = false
    this.element.querySelector('.product-card__title a').setAttribute('href' , `${this.element.dataset.collection}/products/${handle}`)
    let content = ''

    if (price < compareAtPrice) {
      content = `
        <span class="ProductItem__Price Price Price--highlight Text--subdued">
          ${theme.formatMoney(price, theme.moneyFormat)}
        </span>
        <span class="ProductItem__Price Price Price--compareAt Text--subdued">
          ${theme.formatMoney(compareAtPrice, theme.moneyFormat)}
        </span>
      `
    } else if (priceVaries) {
      content = `<span class="ProductItem__Price Price Text--subdued">
          From ${theme.formatMoney(0, theme.moneyFormat)} to ${theme.formatMoney(0, theme.moneyFormat)}
        </span>`
    } else {
      content = `<span class="ProductItem__Price Price Text--subdued">${theme.formatMoney(price, theme.moneyFormat)}</span>`
    }
    this.$price.innerHTML = content
  }

  renderBuyButtons() {
    const selectedSize = this.activeSize

    if (selectedSize) {
      this.$btnAddToCart.removeAttribute('disabled')
      this.$btnAddToCart.classList.remove('Button--secondary')
      this.$btnAddToCart.classList.add('Button--primary')
      this.$btnAddToCart.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="24px" height="24px" xmlns:xlink="http://www.w3.org/1999/xlink" version="1.1" id="Icons" viewBox="0 0 32 32" xml:space="preserve">
      <style type="text/css">
        .st0{fill:none;stroke:#fff;stroke-width:2;stroke-linecap:round;stroke-linejoin:round;stroke-miterlimit:10;}
        
          .st1{fill:none;stroke:#fff;stroke-width:2;stroke-linecap:round;stroke-linejoin:round;stroke-miterlimit:10;stroke-dasharray:3;}
        .st2{fill:none;stroke:#fff;stroke-width:2;stroke-linejoin:round;stroke-miterlimit:10;}
        .st3{fill:none;}
      </style>
      <polygon class="st0" points="26,29 6,29 8,9 24,9 "/>
      <path class="st0" d="M12,13V7c0-2.2,1.8-4,4-4h0c2.2,0,4,1.8,4,4v6"/>
      <rect x="-504" y="-72" class="st3" width="536" height="680"/>
      </svg> <span>${window.languages.productFormAddToCart}</span>`
    } else {
      this.$btnAddToCart.setAttribute('disabled', 'true')
      this.$btnAddToCart.classList.add('Button--secondary')
      this.$btnAddToCart.classList.remove('Button--primary')
      this.$btnAddToCart.textContent = window.languages.selectSize
    }
  }

  renderGeneral() {
    const { color } = this.activeSwatchData

    this.$color.textContent = color

    this.$swatches.forEach(($swatch) => {
      if ($swatch.dataset.handle === this.handle) {
        $swatch.classList.add('product-card__swatch--active')
      } else {
        $swatch.classList.remove('product-card__swatch--active')
      }
    })
  }
  // Renderers END

  /********** Utilities **********/
  static getVariantId(variantId) {
    if (typeof variantId === 'number') {
      return variantId
    }
    return Number(String(variantId).split('/').at(-1))
  }
}

export default ProductCard
